"use client";

import { FeedbackSubmitRequest, FeedbackSubmitRequestType, api } from "@/api";
import { Button, ConfigProvider, Modal } from "antd";
import { useState } from "react";
import styles from "./index.module.css";

type OnboardingModalProps = {
  open: boolean;
  onComplete: (data: OnboardingData) => void;
};

export type OnboardingData = {
  discovery: string;
  profile: string;
};

type AnswersState = Record<string, string>;

type QuestionOption = {
  label: string;
  value: string;
  subtitle?: string;
};

type QuestionConfig = {
  id: string;
  question: string;
  options: QuestionOption[];
};

const ONBOARDING_CONFIG: QuestionConfig[] = [
  {
    id: "discovery",
    question: "How'd you hear about VidMuse?",
    options: [
      { label: "Friend / Colleague", value: "friend" },
      {
        label: "Social Media",
        value: "social_media",
        subtitle: "TikTok, IG, X",
      },
      {
        label: "Creator Reviews",
        value: "creator_reviews",
        subtitle: "YouTube / Blogs",
      },
      { label: "Search Engine", value: "search_engine" },
      { label: "Other", value: "other" },
    ],
  },
  {
    id: "profile",
    question: "You are...",
    options: [
      {
        label: "An Artist",
        value: "artist",
        subtitle: "Visualizing my music",
      },
      {
        label: "A Professional",
        value: "professional",
        subtitle: "Creating for clients",
      },
      {
        label: "A Creator",
        value: "creator",
        subtitle: "YouTube / TikTok / Social",
      },
      {
        label: "An Enthusiast",
        value: "enthusiast",
        subtitle: "Exploring AI art",
      },
    ],
  },
];

export function OnboardingModal({ open, onComplete }: OnboardingModalProps) {
  const [answers, setAnswers] = useState<AnswersState>({});

  const updateAnswer = (questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const getOptionLabel = (questionId: string, value: string) => {
    const question = ONBOARDING_CONFIG.find((q) => q.id === questionId);
    return (
      question?.options.find((option) => option.value === value)?.label || value
    );
  };

  const handleSubmit = async () => {
    const allAnswered = ONBOARDING_CONFIG.every(
      (question) => answers[question.id]
    );
    if (allAnswered) {
      // 完成 onboarding
      const onboardingData: OnboardingData = {
        discovery: answers.discovery,
        profile: answers.profile,
      };
      onComplete(onboardingData);

      // 构建 feedback 数据
      const feedbackContent: Record<string, string> = {};
      ONBOARDING_CONFIG.forEach((question) => {
        const answer = answers[question.id];
        if (answer) {
          feedbackContent[question.question] = getOptionLabel(
            question.id,
            answer
          );
        }
      });

      const feedbackData: FeedbackSubmitRequest = {
        type: FeedbackSubmitRequestType.Onboarding,
        content: JSON.stringify(feedbackContent),
      };

      try {
        // 调用 feedback API
        await api.feedback.submitFeedback(feedbackData);
      } catch (error) {
        console.error("Failed to submit onboarding feedback:", error);
      }
    }
  };

  const canSubmit = ONBOARDING_CONFIG.every((question) => answers[question.id]);

  return (
    <Modal
      open={open}
      centered
      closable={false}
      maskClosable={false}
      footer={null}
      width={600}
      classNames={{
        content: styles.modalContent,
        wrapper: styles.modalWrapper,
      }}
    >
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>VidMuse: Creative Onboarding</h2>
          <p className={styles.subtitle}>
            🎉 Welcome! Help us tailor your studio in 10 seconds.
          </p>
        </div>

        {/* 动态生成问题 */}
        {ONBOARDING_CONFIG.map((question) => (
          <div key={question.id} className={styles.section}>
            <h3 className={styles.sectionTitle}>{question.question}</h3>
            <div className={styles.optionsGrid}>
              {question.options.map((option) => (
                <div
                  key={option.value}
                  className={`${styles.optionCard} ${
                    answers[question.id] === option.value ? styles.selected : ""
                  }`}
                  onClick={() => updateAnswer(question.id, option.value)}
                >
                  <div className={styles.optionLabel}>{option.label}</div>
                  {option.subtitle && (
                    <div className={styles.optionSubtitle}>
                      {option.subtitle}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Submit Button */}
        <div className={styles.actions}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: 48,
                  fontWeight: 600,
                  fontSize: 16,
                  borderRadius: 99,
                  colorPrimary: "#5251f6",
                  colorPrimaryHover: "#4E4DEA",
                  colorPrimaryActive: "#4241C5",
                },
              },
            }}
          >
            <Button
              type="primary"
              block
              disabled={!canSubmit}
              onClick={handleSubmit}
              className={styles.submitButton}
            >
              Get Started 🚀
            </Button>
          </ConfigProvider>
        </div>
      </div>
    </Modal>
  );
}
